import "prosemirror-view/style/prosemirror.css";
import createCache from "@emotion/cache";
import {
  ReactNode,
  useRef,
  useState,
  useCallback,
  forwardRef,
  useImperativeHandle,
  useMemo,
  RefObject,
  useEffect,
} from "react";
import { createRoot, Root } from "react-dom/client";
import { createPortal } from "react-dom";
import { ThemeProvider } from "@mui/material/styles";
import theme from "../../theme";
import { Box, CssBaseline, Button, Typography } from "@mui/material";
import { CacheProvider } from "@emotion/react";
import {
  PictureInPicture2,
  PictureInPicture as PictureInPictureIcon,
} from "lucide-react";

const getFeatureUnavailableReason = () => {
  if (window.location.protocol !== "https:")
    return FeatureUnavailableReasonEnum.USING_UNSECURE_PROTOCOL;
  if (!("documentPictureInPicture" in window))
    return FeatureUnavailableReasonEnum.API_NOT_SUPPORTED;
  return null;
};

export const isPictureInPictureAvailable = (): boolean => {
  return getFeatureUnavailableReason() === null;
};

const ACTIVE_PIP_SESSION_KEY = "faye.activePipComponentId";

const getActivePipId = (): string | null => {
  try {
    return sessionStorage.getItem(ACTIVE_PIP_SESSION_KEY);
  } catch {
    return null;
  }
};

const setActivePipId = (id: string | null): void => {
  try {
    if (id) {
      sessionStorage.setItem(ACTIVE_PIP_SESSION_KEY, id);
    } else {
      sessionStorage.removeItem(ACTIVE_PIP_SESSION_KEY);
    }
  } catch {}
};

export type ReactDocumentPictureInPictureForwardRefType = {
  window: () => Window | undefined;
  isOpen: boolean;
  close: () => void;
  toggle: () => void;
};

export enum FeatureUnavailableReasonEnum {
  USING_UNSECURE_PROTOCOL = "USING_UNSECURE_PROTOCOL",
  API_NOT_SUPPORTED = "API_NOT_SUPPORTED",
}

export type ReactDocumentPictureInPicturePropsType = {
  width?: string | number;
  height?: string | number;
  shareStyles?: boolean;
  onOpen?: () => void;
  onClose?: () => void;
  onResize?: (width: number, height: number) => void;
  featureUnavailableRenderer?:
    | ReactNode
    | ((reason: FeatureUnavailableReasonEnum) => ReactNode);
  buttonRenderer?:
    | ReactNode
    | ((props: {
        open: () => void;
        close: () => void;
        toggle: () => void;
        isOpen: boolean;
      }) => ReactNode);
  children?: ReactNode;
  contentRef?: RefObject<HTMLElement>;
  id?: string;
};

const PictureInPicture = forwardRef<
  ReactDocumentPictureInPictureForwardRefType,
  ReactDocumentPictureInPicturePropsType
>((props, ref) => {
  const reactRoot = useRef<Root>();
  const container = useRef<HTMLDivElement>();
  const pipWindowRef = useRef<Window>();
  const [isOpen, setIsOpen] = useState(false);

  const componentId = useMemo(() => {
    if (props.id) return props.id;
    if (props.contentRef?.current) {
      const element = props.contentRef.current;
      const rect = element.getBoundingClientRect();
      return `pip-${element.tagName.toLowerCase()}-${Math.floor(
        rect.top,
      )}-${Math.floor(rect.left)}`;
    }
    return `pip-${Math.random().toString(36).substr(2, 9)}`;
  }, [props.id, props.contentRef]);

  const absoluteDimensions = useMemo(() => {
    const parseDim = (
      dim: string | number | undefined,
      fallback: number,
      total: number,
    ) => {
      if (typeof dim === "number") return dim;
      if (typeof dim === "string") {
        if (dim.endsWith("px")) return parseInt(dim);
        if (dim.endsWith("%")) return total * (parseInt(dim) / 100);
      }
      return fallback;
    };
    return {
      width: parseDim(props.width, 500, window.innerWidth),
      height: parseDim(props.height, 400, window.innerHeight),
    };
  }, [props.width, props.height]);

  const featureUnavailableReason = getFeatureUnavailableReason();

  // Function to check if PiP window is actually open
  const checkPipWindowState = useCallback(() => {
    const actuallyOpen = !!(
      pipWindowRef.current && !pipWindowRef.current.closed
    );
    const storedActivePipId = getActivePipId();
    const isThisComponentActive = storedActivePipId === componentId;

    // Component should be open if:
    // 1. Window is actually open AND this component is the active one in session storage
    // 2. OR window is open and no active component is stored (fallback)
    const shouldBeOpen =
      actuallyOpen && (isThisComponentActive || !storedActivePipId);

    if (shouldBeOpen !== isOpen) {
      setIsOpen(shouldBeOpen);
      if (!shouldBeOpen) {
        if (isThisComponentActive) {
          setActivePipId(null);
        }
        props.onClose?.();
      }
    }
    return shouldBeOpen;
  }, [isOpen, props.onClose, componentId]);

  // Check for existing PiP window on component mount
  useEffect(() => {
    const storedActivePipId = getActivePipId();
    const isThisComponentActive = storedActivePipId === componentId;

    // @ts-ignore
    if (window.documentPictureInPicture?.window && isThisComponentActive) {
      // @ts-ignore
      const existingWindow = window.documentPictureInPicture.window;
      if (existingWindow && !existingWindow.closed) {
        pipWindowRef.current = existingWindow;
        setIsOpen(true);

        // Re-attach event listeners
        existingWindow.addEventListener("pagehide", () => {
          setIsOpen(false);
          setActivePipId(null);
          props.onClose?.();
        });

        existingWindow.addEventListener("resize", () => {
          props.onResize?.(
            existingWindow.innerWidth,
            existingWindow.innerHeight,
          );
        });
      } else {
        setActivePipId(null);
      }
    }
  }, [componentId, props.onClose, props.onResize]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        setTimeout(() => {
          checkPipWindowState();
        }, 100);
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [checkPipWindowState]);

  const close = useCallback(() => {
    if (!pipWindowRef.current || pipWindowRef.current.closed) {
      setIsOpen(false);
      setActivePipId(null);
      return;
    }
    pipWindowRef.current.close();
    setIsOpen(false);
    setActivePipId(null);
    props.onClose?.();
  }, [props.onClose]);

  const open = useCallback(async () => {
    // @ts-ignore
    if (!window.documentPictureInPicture) {
      return;
    }

    // @ts-ignore
    const pipWin = await window.documentPictureInPicture.requestWindow({
      ...absoluteDimensions,
    });

    if (!pipWin) return;

    pipWindowRef.current = pipWin;

    // Basic body styles
    pipWin.document.body.style.margin = "0";
    pipWin.document.body.style.padding = "0";
    pipWin.document.body.style.overflow = "hidden";

    if (props.shareStyles) {
      document
        .querySelectorAll(
          'link[rel="stylesheet"], link[rel="icon"], link[as="font"]',
        )
        .forEach((el) => pipWin.document.head.appendChild(el.cloneNode(true)));

      // Clone any font-face styles in <style> tags
      document.querySelectorAll("style").forEach((styleEl) => {
        if (styleEl.textContent?.includes("@font-face")) {
          pipWin.document.head.appendChild(styleEl.cloneNode(true));
        }
      });

      // Clone meta tags (for fonts, viewport, etc)
      document.querySelectorAll("meta").forEach((meta) => {
        pipWin.document.head.appendChild(meta.cloneNode(true));
      });
    }

    const emotionCache = createCache({
      key: "mui-pip",
      container: pipWin.document.head,
    });

    // Create container div in PiP window
    const rootContainer = pipWin.document.createElement("div");
    rootContainer.id = "react-root";
    rootContainer.style.width = "100%";
    rootContainer.style.height = "100%";
    pipWin.document.body.appendChild(rootContainer);

    container.current = rootContainer;

    /*
    We ust mount a new react root.  We cannot pass in a component or clone the element
    We must also create a new emotion cache.  This is because the emotion cache is created in the main window.
    */
    reactRoot.current = createRoot(rootContainer);
    reactRoot.current.render(
      <CacheProvider value={emotionCache}>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <Box
            sx={{
              padding: "24px",
              backgroundColor: "#FFF",
              paddingBottom: "0px",
            }}
          >
            {props.children}
          </Box>
        </ThemeProvider>
      </CacheProvider>,
    );

    // Events - Listen for when the PiP window is actually closed
    pipWin.addEventListener("pagehide", () => {
      setIsOpen(false);
      setActivePipId(null);
      props.onClose?.();
    });

    pipWin.addEventListener("resize", () => {
      props.onResize?.(pipWin.innerWidth, pipWin.innerHeight);
    });

    setIsOpen(true);
    setActivePipId(componentId); // Track this component as the active PiP
    props.onOpen?.();
  }, [
    absoluteDimensions,
    props.children,
    props.shareStyles,
    props.onOpen,
    props.onResize,
    props.onClose,
    componentId,
  ]);

  const toggle = useCallback(() => {
    return isOpen ? close() : open();
  }, [isOpen, open, close]);

  useImperativeHandle(
    ref,
    () => ({
      window: () => pipWindowRef.current,
      isOpen: isOpen,
      close,
      toggle,
    }),
    [isOpen, close, toggle],
  );

  if (featureUnavailableReason !== null && !props.featureUnavailableRenderer)
    return null;

  if (featureUnavailableReason !== null) {
    return typeof props.featureUnavailableRenderer === "function"
      ? props.featureUnavailableRenderer(featureUnavailableReason)
      : props.featureUnavailableRenderer;
  }

  const buttonRenderer =
    typeof props.buttonRenderer === "function"
      ? props.buttonRenderer({
          open,
          close,
          toggle,
          isOpen,
        })
      : props.buttonRenderer;

  useEffect(() => {
    if (isOpen && props.contentRef?.current) {
      const contentElement = props.contentRef.current;
      const originalPosition = contentElement.style.position;

      if (!originalPosition || originalPosition === "static") {
        contentElement.style.position = "relative";
      }

      return () => {
        contentElement.style.position = originalPosition;
      };
    }
  }, [isOpen, props.contentRef]);

  return (
    <>
      <div style={{ position: "relative" }}>{buttonRenderer}</div>
      {isOpen && props.contentRef?.current && (
        <OverlayPortal container={props.contentRef.current} onClose={close} />
      )}
    </>
  );
});

const OverlayPortal = ({
  container,
  onClose,
}: {
  container: HTMLElement;
  onClose: () => void;
}) => {
  return createPortal(
    <Box
      sx={{
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        backgroundColor: "rgba(0, 0, 0, 0.90)",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
        color: "white",
        padding: "24px",
        borderRadius: "12px",
        cursor: "default",
      }}
    >
      <Box sx={{ textAlign: "center", width: "100%" }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            marginBottom: "16px",
            gap: "16px",
          }}
        >
          <Box
            sx={{
              width: 48,
              height: 48,
              backgroundColor: "rgba(255, 255, 255, 0.1)",
              borderRadius: "8px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <PictureInPicture2 size={24} color="white" />
          </Box>

          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              color: "white",
              fontSize: "1.25rem",
            }}
          >
            Picture-in-Picture is active
          </Typography>
        </Box>

        <Typography
          variant="body2"
          sx={{
            color: "rgba(255, 255, 255, 0.8)",
            marginBottom: "24px",
            lineHeight: 1.4,
            fontSize: "0.875rem",
            maxWidth: "80%",
            margin: "0 auto 24px auto",
          }}
        >
          Your content is currently active in a separate browser window.
        </Typography>

        <Button
          variant="contained"
          onClick={onClose}
          startIcon={<PictureInPictureIcon size={16} />}
          sx={{
            backgroundColor: "#4f46e5",
            "&:hover": {
              backgroundColor: "#4338ca",
            },
            borderRadius: "8px",
            paddingTop: "8px",
            paddingBottom: "8px",
            paddingLeft: "24px",
            paddingRight: "24px",
            fontSize: "0.875rem",
            fontWeight: 600,
            textTransform: "none",
          }}
        >
          Bring it back
        </Button>
      </Box>
    </Box>,
    container,
  );
};

export default PictureInPicture;
